import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../api/axios';
import useGetUIErrorMessage from '../../../hooks/useGetUIErrorMessage';
import { IApiResponseError, IApiState } from '../../../types/api';
import { ITeamMember } from '../../../types/teamMember';
import { RootState } from '../..';
import { showAlert } from '../appToast/appToastSlice';

interface IEnableOutletResponse {
  status?: boolean;
  message?: string;
  data?: {
    id?: string;
    name?: string;
    address?: string;
    city?: string;
    state?: string;
    isActive?: boolean;
    createdAt?: string;
    updatedAt?: string;
    teamMembers?: ITeamMember[];
  };

  errors?: IApiResponseError[];
}

const initialState: IApiState<IEnableOutletResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const enableOutletAsync = createAsyncThunk(
  'outlet/enable',
  async (
    { outletId, pin }: { outletId: string; pin: string },
    { rejectWithValue, dispatch, getState }
  ) => {
    try {
      const state = getState() as RootState;

      const accessToken = state.auth.value?.data?.token?.accessToken;

      const businessId = localStorage.getItem('businessId');

      const config = {
        headers: {
          Authorization: `Token ${accessToken}`,
          'X-Business-Id': businessId,
        },
      };

      const { data } = await api.put<IEnableOutletResponse>(
        `/outlets/${outletId}/enable`,
        { pin: pin },
        config
      );

      dispatch(
        showAlert({
          status: 'success',
          description: `${data.data?.name} enabled successfully`,
        })
      );

      return data;
    } catch (err) {
      const error = err as AxiosError<IEnableOutletResponse>;

      const errorMessage = useGetUIErrorMessage({
        message: error.response?.data?.message,
        errors: error.response?.data?.errors,
      });

      dispatch(
        showAlert({
          status: 'error',
          description: errorMessage,
          type: 'drawer',
        })
      );

      return rejectWithValue(error.response?.data);
    }
  }
);

export const outletEnableSlice = createSlice({
  name: 'outlet/enable',
  initialState,
  reducers: {
    resetOutletEnable(state) {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(enableOutletAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(enableOutletAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload as IEnableOutletResponse;
      })
      .addCase(enableOutletAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.value = action.payload as IEnableOutletResponse;
        state.error = action.error.message;
      });
  },
});

export const { resetOutletEnable } = outletEnableSlice.actions;

export default outletEnableSlice.reducer;
